// Shared gtag.js bootstrap used by every Google tag we load through gtag
// (GA4 in firebase.ts, Floodlight/Campaign Manager in floodlight.ts).
//
// gtag.js is ONE library regardless of the id in its script URL: every
// `gtag('config', <id>)` registers another target on the same runtime. Each
// caller still injects the script tagged with its own id, as Google's snippets
// do, so a tag keeps working if the other one is disabled per environment.

export type GtagFn = (...args: unknown[]) => void;

const GTAG_BASE = "https://www.googletagmanager.com/gtag/js?id=";

type GtagWindow = { dataLayer?: unknown[]; gtag?: GtagFn };

/**
 * Return window.gtag, creating the canonical shim (push the raw `arguments`
 * object onto dataLayer) if nothing defined it yet. Browser-only: callers
 * must not invoke this during SSR.
 */
export function ensureGtag(): GtagFn {
  const w = window as unknown as GtagWindow;
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      (w.dataLayer as unknown[]).push(arguments);
    } as unknown as GtagFn;
  }
  return w.gtag;
}

/** Inject the gtag.js script for `id` once. Queued commands flush on load. */
export function loadGtagScript(id: string): void {
  const src = `${GTAG_BASE}${id}`;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}
